import React, { useState, useMemo, useCallback } from "react";
import "./TransactionReportPage.css";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import vi from "date-fns/locale/vi";
import TransactionDetailModal from "../../components/TransactionDetailModal/TransactionDetailModal";
import { exportToExcel } from "../../utils/excel";
import { format } from "date-fns";
import { api } from "../../utils/api";
import { useLocalSearch } from "./../../hooks/useLocalSearch";
import { useToast } from "./../../components/Toast/ToastContainer";
import Pagination from "../../components/Pagination/Pagination";

registerLocale("vi", vi);

const TransactionReportPage = () => {
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const toast = useToast();
  const [data, setData] = useState([]);
  // --- THÊM MỚI: Tạo dữ liệu đã được Việt hóa để tìm kiếm ---
  const processedData = useMemo(() => {
    return data.map((row) => {
      // Logic map trạng thái sang tiếng Việt (khớp với hàm renderStatus của bạn)
      let statusVi = row.status;
      if (row.status === "SUCCESS") statusVi = "Thành công";
      else if (row.status === "FAILED" || row.status === "REJECTED") statusVi = "Thất bại";
     
      // ... bạn có thể thêm các case khác nếu cần

      return {
        ...row, // Giữ nguyên dữ liệu gốc để không lỗi bảng

        // Thêm các trường phụ để phục vụ tìm kiếm (Search keys)
        search_status: statusVi,
        search_type:
          row.transaction_type === "CASH_IN" ? "Nộp tiền" : "Rút tiền",
        search_amount: row.amount ? row.amount.toString() : "", // Để tìm theo số tiền
        search_date: row.created_at
          ? format(new Date(row.created_at), "dd/MM/yyyy")
          : "", // Tìm theo ngày hiển thị
      };
    });
  }, [data]);
  const { searchTerm, setSearchTerm, filteredData } =
    useLocalSearch(processedData);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    page_size: 10,
  });

  const [filters, setFilters] = useState({
    transaction_ref: "",
    core_ref: "",
    transaction_type: "",
    customer_account: "",
    from_date: null,
    to_date: null,
    status: "",
  });

  const fetchTransactionReports = useCallback(
    async (pageNumber = 1) => {
      setLoading(true);
      try {
        // 1. Xây dựng Query Parameters
        const params = {
          page: pageNumber,
          page_size: pagination.page_size,
          transaction_ref: filters.transaction_ref || "",
          core_ref: filters.core_ref || "",
          transaction_type: filters.transaction_type || "",
          account_number: filters.customer_account || "",
          status: filters.status || "",
        };

        // Định dạng ngày sang ISO cho Server
        if (filters.from_date) {
          params.from_date = format(filters.from_date, "yyyy-MM-dd'T'00:00:00");
        }
        if (filters.to_date) {
          params.to_date = format(filters.to_date, "yyyy-MM-dd'T'23:59:59");
        }

        // Biến đối tượng params thành chuỗi query: ?page=1&transaction_type=CASH_IN...
        const queryString = new URLSearchParams(
          Object.entries(params).filter(([_, v]) => v !== "") // Chỉ lấy các field có giá trị
        ).toString();

        // 2. Gọi API bằng fetch
        const response = await fetch(
          `${api.baseUrl}/agent/reports/transactions/?${queryString}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access_token")}`, // Mở ra nếu có token
            },
          }
        );

        console.log(response);

        // 3. Kiểm tra lỗi HTTP (fetch không tự throw lỗi khi status 4xx, 5xx)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        // 4. Cập nhật State
        if (result && result.data) {
          setData(result.data);
          setPagination({
            total: result.total,
            page: result.page,
            page_size: result.page_size,
          });
        }
      } catch (error) {
        console.error("Lỗi khi fetch báo cáo:", error);
        setData([]); // Xóa bảng nếu lỗi
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.page_size]
  );
  // useEffect(() => {
  //   fetchTransactionReports();
  // }, []); // Gọi lần đầu khi mount

  // Kiểm tra xem tất cả các trường filter có trống không
  const isFilterEmpty = useMemo(() => {
    return (
      !filters.transaction_ref.trim() &&
      !filters.core_ref.trim() &&
      !filters.transaction_type &&
      !filters.customer_account.trim() &&
      !filters.status &&
      !filters.from_date &&
      !filters.to_date
    );
  }, [filters]);

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleDateChange = (date, name) => {
    setFilters((prev) => {
      // Clone state cũ
      const newFilters = { ...prev, [name]: date };

      // 1. Nếu đang chọn 'Từ ngày' (from_date)
      if (name === "from_date") {
        // Nếu đã có 'Đến ngày' rồi, mà ngày mới chọn lại LỚN HƠN 'Đến ngày'
        if (prev.to_date && date && date > prev.to_date) {
          // => Reset 'Đến ngày' về null (bắt chọn lại)
          newFilters.to_date = null;
        }
      }

      // 2. Nếu đang chọn 'Đến ngày' (to_date)
      if (name === "to_date") {
        // Nếu đã có 'Từ ngày' rồi, mà ngày mới chọn lại NHỎ HƠN 'Từ ngày'
        if (prev.from_date && date && date < prev.from_date) {
          // => Reset 'Từ ngày' về null
          newFilters.from_date = null;
        }
      }

      return newFilters;
    });
  };

  const handleReset = () => {
    setFilters({
      transaction_ref: "",
      core_ref: "",
      transaction_type: "",
      customer_account: "",
      from_date: null,
      to_date: null,
      status: "",
    });
  };

  const handleSearch = () => {
    // Sử dụng lại biến isFilterEmpty đã được tính toán ở useMemo
    if (isFilterEmpty) {
      toast.error("Vui lòng nhập ít nhất một thông tin để tìm kiếm.");
      return;
    }

    fetchTransactionReports(1);
  };

  // const handleExportExcel = async () => {
  //   const response = await fetch(
  //     `${api.baseUrl}/agent/reports/transactions/all`,
  //     {
  //       method: "GET",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Bearer ${localStorage.getItem("access_token")}`, // Mở ra nếu có token
  //       },
  //     }
  //   );

  //   // 3. Kiểm tra lỗi HTTP (fetch không tự throw lỗi khi status 4xx, 5xx)
  //   if (!response.ok) {
  //     throw new Error(`HTTP error! status: ${response.status}`);
  //   }

  //   const result = await response.json();
  //   const header = [
  //     { label: "Số giao dịch", key: "transaction_ref" },
  //     { label: "Số REF hạch toán", key: "core_ref" },
  //     { label: "Ngày giao dịch", key: "created_at" },
  //     { label: "Trạng thái", key: "status" },
  //     { label: "Số tiền", key: "amount" },
  //     { label: "Phí", key: "fee" },
  //     { label: "Loại giao dịch", key: "transaction_type" },
  //     { label: "CIF Khách hàng", key: "customer_cif" },
  //     { label: "Số tài khoản", key: "customer_account" },
  //     { label: "Tên tài khoản", key: "customer_name" },
  //     { label: "Người tạo", key: "created_by" },
  //   ];

  //   exportToExcel({
  //     header,
  //     data: result.data,
  //     fileName: `Bao_cao_giao_dich_${format(new Date(), "ddMMyyyy")}.xlsx`,
  //   });
  // };
  // Hàm chuyển đổi dữ liệu từ dòng được chọn sang định dạng Modal cần
  const handleExportExcel = async () => {
    // 1. Kiểm tra điều kiện bắt buộc: Phải có ít nhất 1 filter mới cho xuất
    if (isFilterEmpty) {
      toast.error(
        "Vui lòng nhập ít nhất một điều kiện lọc trước khi xuất Excel."
      );
      return;
    }

    try {
      setLoading(true);

      // 2. Gọi API lấy toàn bộ dữ liệu
      const response = await fetch(
        `${api.baseUrl}/agent/reports/transactions/all`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      let rawData = result.data || [];

      // 3. LỌC THỦ CÔNG: Khớp dữ liệu response với filters hiện tại của người dùng
      const filteredExportData = rawData.filter((item) => {
        const matchRef =
          !filters.transaction_ref ||
          item.transaction_ref
            ?.toLowerCase()
            .includes(filters.transaction_ref.toLowerCase());

        const matchCore =
          !filters.core_ref ||
          item.core_ref?.toLowerCase().includes(filters.core_ref.toLowerCase());

        const matchType =
          !filters.transaction_type ||
          item.transaction_type === filters.transaction_type;

        const matchAccount =
          !filters.customer_account ||
          item.customer_account?.includes(filters.customer_account);

        const matchStatus = !filters.status || item.status === filters.status;

        // Lọc theo ngày (Date)
        const itemDate = new Date(item.created_at);
        const matchFromDate =
          !filters.from_date || itemDate >= filters.from_date;
        // Tăng to_date lên cuối ngày để so sánh chính xác
        const matchToDate =
          !filters.to_date ||
          itemDate <= new Date(filters.to_date.setHours(23, 59, 59));

        return (
          matchRef &&
          matchCore &&
          matchType &&
          matchAccount &&
          matchStatus &&
          matchFromDate &&
          matchToDate
        );
      });

      if (filteredExportData.length === 0) {
        alert(" không có dữ liệu phù hợp với bộ lọc để xuất Excel.");
        return;
      }

      // 4. Định nghĩa Header
      const header = [
        { label: "Số giao dịch", key: "transaction_ref" },
        { label: "Số REF hạch toán", key: "core_ref" },
        { label: "Ngày giao dịch", key: "created_at" },
        { label: "Trạng thái", key: "status" },
        { label: "Số tiền", key: "amount" },
        { label: "Phí", key: "fee" },
        { label: "Loại giao dịch", key: "transaction_type" },
        { label: "CIF Khách hàng", key: "customer_cif" },
        { label: "Số tài khoản", key: "customer_account" },
        { label: "Tên tài khoản", key: "customer_name" },
        { label: "Người tạo", key: "created_by" },
      ];

      // 5. Mapping dữ liệu sang tiếng Việt giống hệt bảng hiển thị
      const excelData = filteredExportData.map((row) => ({
        ...row,
        created_at: row.created_at
          ? format(new Date(row.created_at), "dd/MM/yyyy HH:mm:ss")
          : "-",
        status: renderStatus(row.status),
        transaction_type:
          row.transaction_type === "CASH_IN" ? "Nộp tiền" : "Rút tiền",
        amount: new Intl.NumberFormat("vi-VN").format(row.amount || 0),
        fee: new Intl.NumberFormat("vi-VN").format(row.fee || 0),
      }));

      // 6. Thực hiện xuất file
      exportToExcel({
        header,
        data: excelData,
        fileName: `Bao_cao_giao_dich_loc_thu_cong_${format(
          new Date(),
          "ddMMyyyy_HHmm"
        )}.xlsx`,
      });
    } catch (error) {
      console.error("Lỗi khi lọc và xuất Excel:", error);
      alert("Có lỗi xảy ra trong quá trình xử lý dữ liệu xuất Excel.");
    } finally {
      setLoading(false);
    }
  };

  const getModalData = (row) => {
    if (!row) return null;

    // Lấy text trạng thái từ hàm renderStatus có sẵn
    const statusText = renderStatus(row.status);

    return {
      soGiaoDich: row.transaction_ref,
      soRef: row.core_ref || "N/A", // Xử lý nếu null
      ngayGD: row.created_at
        ? format(new Date(row.created_at), "dd/MM/yyyy")
        : "-",
      gioGD: row.created_at
        ? format(new Date(row.created_at), "HH:mm:ss")
        : "-",
      soTien: new Intl.NumberFormat("vi-VN").format(row.amount || 0) + " VNĐ",
      phiVat: new Intl.NumberFormat("vi-VN").format(row.fee || 0) + " VNĐ",
      loaiGD: row.transaction_type === "CASH_IN" ? "Nộp tiền" : "Rút tiền",
      cif: row.customer_cif,
      soTK: row.customer_account,
      tenTK: row.customer_name,
      userThucHien: row.created_by,
      daiLy: "Đại lý mặc định", // Hoặc row.agent_name nếu API có trả về
      trangThai: statusText, // Truyền text tiếng Việt vào (VD: Thành công)
    };
  };
  // Helper hiển thị label trạng thái
  //   const renderStatus = (status) => {
  //     const statusMap = {
  //       SUCCESS: { text: "Thành công", class: "status-success" },
  //       FAILED: { text: "Thất bại", class: "status-failed" },
  //       PENDING: { text: "Chờ duyệt", class: "status-pending" },
  //     };
  //     return statusMap[status]?.text || status;
  //   };

  const renderStatus = (status) => {
    const statusMap = {
      CREATED: {
        // text: "Khởi tạo QR code",
        text: "Chờ tra soát",
        class: "status-created",
      },
      PENDING_CUSTOMER: {
        // text: "Chờ quét mã",
        text: "Chờ tra soát",
        class: "status-pending-customer",
      },
      CUSTOMER_CONFIRMED: {
        // text: "Khách đã xác nhận",
        text: "Chờ tra soát",
        class: "status-confirmed",
      },
      PENDING_APPROVAL: {
        // text: "Chờ Duyệt",
        text: "Chờ tra soát",
        class: "status-pending-approval",
      },
      PROCESSING: {
        // text: "Đang xử lý (Core)",
        text: "Chờ tra soát",
        class: "status-processing",
      },
      SUCCESS: {
        text: "Thành công",
        class: "status-success",
      },
      FAILED: {
        text: "Thất bại",
        class: "status-failed",
      },
      REJECTED: {
        // text: "Bị từ chối",
        text: "Thất bại",
        class: "status-failed",
      },
    };

    const config = statusMap[status] || { text: status, class: "" };

    return config.text;
  };

  return (
    <div className="tr-container">
      <h2 className="page-title">Báo cáo giao dịch</h2>

      <div className="tr-filter-card">
        <div className="tr-grid">
          <div className="tr-item">
            <label>Số giao dịch</label>
            <input
              name="transaction_ref"
              value={filters.transaction_ref}
              onChange={handleChange}
              placeholder="Ví dụ: AGB2025..."
            />
          </div>

          <div className="tr-item">
            <label>Số REF hạch toán</label>
            <input
              name="core_ref"
              value={filters.core_ref}
              onChange={handleChange}
              placeholder="Ví dụ: FT..."
            />
          </div>

          <div className="tr-item">
            <label>Loại giao dịch</label>
            <select
              name="transaction_type"
              value={filters.transaction_type}
              onChange={handleChange}
            >
              <option value="">Tất cả</option>
              <option value="CASH_IN">Nộp tiền</option>
              <option value="CASH_OUT">Rút tiền</option>
            </select>
          </div>

          <div className="tr-item">
            <label>Số tài khoản khách hàng</label>
            <input
              name="customer_account"
              value={filters.customer_account}
              onChange={handleChange}
              placeholder="Nhập số tài khoản"
            />
          </div>

          <div className="tr-item">
            <label>Từ ngày</label>
            <div className="tr-date-wrapper">
              <img
                src="/icons/icons8-calendar-100.png"
                className="tr-date-icon"
                alt=""
              />
              <DatePicker
                selected={filters.from_date}
                onChange={(d) => handleDateChange(d, "from_date")}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                className="tr-datepicker"
                locale="vi"
                selectsStart
                startDate={filters.from_date}
                endDate={filters.to_date}
                maxDate={filters.to_date ? filters.to_date : new Date()}
              />
            </div>
          </div>

          <div className="tr-item">
            <label>Đến ngày</label>
            <div className="tr-date-wrapper">
              <img
                src="/icons/icons8-calendar-100.png"
                className="tr-date-icon"
                alt=""
              />
              <DatePicker
                selected={filters.to_date}
                onChange={(d) => handleDateChange(d, "to_date")}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                className="tr-datepicker"
                locale="vi"
                selectsEnd
                startDate={filters.from_date}
                endDate={filters.to_date}
                minDate={filters.from_date}
                maxDate={new Date()}
              />
            </div>
          </div>

          <div className="tr-item">
            <label>Trạng thái</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleChange}
            >
              <option value="">Tất cả</option>
              <option value="SUCCESS">Thành công</option>
              <option value="FAILED">Thất bại</option>
              <option value="PENDING">Chờ duyệt</option>
            </select>
          </div>
        </div>

        <div className="tr-actions">
          <button className="btn-secondary" onClick={handleReset}>
            Xóa lọc
          </button>
          <button
            className="btn-primary"
            onClick={handleSearch}
            disabled={loading} // Áp dụng logic tại đây
          >
            {loading ? "Đang tải..." : "Tìm kiếm"}
          </button>
        </div>
      </div>

      <div className="tr-result-header">
        <h3 className="result-title">Kết quả tìm kiếm ({pagination.total})</h3>
        <div className="tr-actions-right">
          <button className="excel-btn" onClick={handleExportExcel}>
            Xuất Excel
          </button>
          <div className="tr-search-inline">
            <img
              src="/icons/icons8-search-100.png"
              className="tr-search-icon"
              alt=""
            />
            <input
              placeholder="Tìm kiếm nhanh..."
              value={searchTerm} // Binding giá trị
              onChange={(e) => setSearchTerm(e.target.value)} // Cập nhật state khi gõ
            />
          </div>
        </div>
      </div>

      <div className="tr-table-wrapper">
        <table className="tr-table">
          <thead>
            <tr>
              <th>Thao tác</th>
              <th>Số giao dịch</th>
              <th>Số REF hạch toán</th>
              <th>Ngày giao dịch</th>
              <th>Trạng thái</th>
              <th>Số tiền</th>
              <th>Phí</th>
              <th>Loại giao dịch</th>
              <th>CIF</th>
              <th>Số tài khoản</th>
              <th>Tên tài khoản</th>
              <th>Người thực hiện</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((row, i) => (
              <tr key={i}>
                <td>
                  <img
                    src="/icons/icons8-eye-100.png"
                    className="eye-icon"
                    alt="Xem"
                    onClick={() => {
                      setSelectedRow(row);
                      setOpenDetail(true);
                    }}
                  />
                </td>
                <td>{row.transaction_ref}</td>
                <td>{row.core_ref}</td>
                <td>
                  {row.created_at
                    ? format(new Date(row.created_at), "dd/MM/yyyy HH:mm:ss")
                    : "-"}
                </td>
                <td>{renderStatus(row.status)}</td>
                <td>{new Intl.NumberFormat("vi-VN").format(row.amount)}</td>
                <td>{new Intl.NumberFormat("vi-VN").format(row.fee)}</td>
                <td>
                  {row.transaction_type === "CASH_IN" ? "Nộp tiền" : "Rút tiền"}
                </td>
                <td>{row.customer_cif}</td>
                <td>{row.customer_account}</td>
                <td>{row.customer_name}</td>
                <td>{row.created_by}</td>
              </tr>
            ))}
            {filteredData.length === 0 && !loading && (
              <tr>
                <td colSpan="12" style={{ textAlign: "center" }}>
                  Không có dữ liệu
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={pagination.page}
        pageSize={pagination.page_size}
        totalItems={pagination.total}
        filteredCount={filteredData.length}
        isSearching={!!searchTerm}
        loading={loading}
        onPageChange={(newPage) => fetchTransactionReports(newPage)}
      />

      <TransactionDetailModal
        open={openDetail}
        onClose={() => setOpenDetail(false)}
        // THAY ĐỔI Ở ĐÂY: Gọi hàm getModalData bọc lấy selectedRow
        data={getModalData(selectedRow)}
        // Thêm callback cho nút Truy vấn (nếu cần xử lý logic sau này)
        onQuery={() => {
          console.log(
            "Thực hiện truy vấn cho giao dịch:",
            selectedRow?.transaction_ref
          );
          // Logic gọi API tra soát ở đây
        }}
      />
    </div>
  );
};

export default TransactionReportPage;
